import type { SupabaseClient } from "@supabase/supabase-js";

import { DUTY_PING_COLUMNS, mapDutyPing, type DutyPing } from "./duty-pings";
import { LIVE_TRAIL_PAINT_CAP } from "./duty-live-presentation";
import {
  commandFocusEvent,
  mergeCommandFocusEvents,
  toCommandFocusTickFeatureCollection,
  toGradientTrailFeatureCollection,
  type CommandFocusEvent,
  type CommandFocusEventInput,
  type CommandFocusTrailCollection,
} from "./command-focus";
import { planLiveTrails, type LiveTrailPlan } from "./live-duty-map";
import { mapScanPoint, SCAN_POINT_COLUMNS, type PointFeatureCollection } from "./map-points";
import { fetchTimeclockLocationPoints } from "./timeclock-location";
import { freeDutyMarkerId } from "./free-duty-live";
import { MAP_THEME } from "../lib/map-theme";
import {
  REPLAY_RETENTION_ALLOWED_DAYS,
  REPLAY_RETENTION_DEFAULT_DAYS,
  replayRetentionDays,
} from "./replay-retention";

export {
  REPLAY_RETENTION_ALLOWED_DAYS,
  REPLAY_RETENTION_DEFAULT_DAYS,
  replayRetentionDays,
} from "./replay-retention";

// Replay desk (lock Q8 third desk, Q12 retention, session 2 camera).
//
// Replay is after-action. It scrubs ONE subject over ONE window like weather radar: what was
// already recorded, played back. It does not forecast, and it is not a second Live CAD.
//
// Two honesty rules shape almost everything here.
//
// 1. A day outside the account retention calendar is UNAVAILABLE, not empty. An empty tape and
//    a tape we are not allowed to read look identical on a map, and only one of them means
//    "nobody was there". `resolveReplayWindow` returns a distinct unavailable status rather
//    than an empty sample list so the desk can say which one it is.
//
// 2. The playhead sits on the last real sample at or before the scrub time. It does NOT
//    interpolate between two pings. Interpolation would draw an officer at a coordinate no
//    device ever reported, which is exactly the invented position this product refuses
//    everywhere else. A stepped playhead is less pretty and it is true.
//
// Retention is enforced here as a CONSOLE clamp. The duty_location_pings SELECT policy has no
// time bound (it gates on account membership plus schedule.manage, or platform access), so the
// window is a product promise this desk keeps, not a database guarantee. Said plainly so nobody
// mistakes it for enforcement.

/** Samples handed to the scrubber. Enough to read motion, few enough to stay responsive. */
export const REPLAY_SLIDER_MAX_SAMPLES = 240;

/** Per-tape sample ceiling, same Y1 spirit as Focus. A month of crumbs is not one tape. */
export const REPLAY_TAPE_POINT_LIMIT = 500;

const DAY_MS = 86_400_000;

export type ReplaySubject =
  | {
      kind: "officer";
      guardRef: string;
      routeAssignmentIds: readonly string[];
      label: string;
    }
  | { kind: "call"; callId: string; callRef: string }
  | { kind: "site"; siteId: string; label: string }
  /**
   * Officers with no site. Lock Q9: never folded into a parcel and never snapped to one. The
   * Unassigned tape draws at true coordinates or it does not draw.
   */
  | { kind: "unassigned"; label: string };

// --- Subject pickers (follow-on W3) ---------------------------------------------------------
//
// Until now officer and Call subjects arrived ONLY by inheritance from an open Focus, so an
// operator who wanted last Tuesday's tape for a guard had to first find that guard on the Live
// desk, focus them, and then open Replay. Replay is a desk, not a Focus accessory; these builders
// turn the lists the desk already holds into subjects it can be pointed at directly.
//
// Two rules bind every option below.
//
// 1. Nothing an operator reads carries a uuid, a raw coordinate, or an auth user id. Officers are
//    named by the retained subject RPC's redacted `guard_label`; Calls by `call_ref`, the
//    `NN-NNNN` number the Calls desk already prints. Assignment ids stay behind the picker as tape
//    scope. A Call with no ref is dropped rather than shown as an id.
// 2. An empty list is a refusal, not an empty dropdown. `replay*OptionsNote` says WHY a kind has
//    nothing in it, so the desk names the missing retained subject kind rather than showing a
//    picker that silently does nothing when clicked.

/** The kinds an operator can point Replay at. */
export type ReplaySubjectKind = "site" | "officer" | "call" | "unassigned";

/** An operator's explicit subject choice, before it is resolved to a labelled `ReplaySubject`. */
export type ReplaySubjectPick =
  | { kind: "site"; siteId: string }
  | { kind: "officer"; guardRef: string }
  | { kind: "call"; callId: string }
  | { kind: "unassigned" };

export interface ReplayOfficerOption {
  guardRef: string;
  routeAssignmentIds: readonly string[];
  /** Redacted person name, with a short ref qualifier only when names collide. */
  label: string;
}

export interface ReplayOfficerSubject {
  guardRef: string;
  guardLabel: string;
  routeAssignmentIds: readonly string[];
}

export interface ReplayCallOption {
  callId: string;
  callRef: string;
  /** `Call 24-0117 · North Campus`, already assembled for the dropdown. */
  label: string;
}

/**
 * The only fields a Replay Call picker reads.
 *
 * Named so the map can be typed against THIS rather than against the whole `DutyCallSummary`,
 * which also carries a Call's location text, dispatcher note, and assignment state. None of that
 * belongs on the map, and the cheapest way to keep it off is to make it unreachable.
 */
export interface ReplayCallSource {
  callId: string;
  callRef: string | null;
  site?: string | null;
  updatedAt?: string | null;
}

/**
 * Most officers and Calls a Replay dropdown will offer.
 *
 * The Calls read alone can return three hundred rows, and a three-hundred-entry select on a
 * vehicle screen is not a picker. Both lists are cut to the most recent of them and the remainder
 * is NAMED, the same discipline the twelve-tape cap follows: an operator told "+41 older Calls not
 * listed" knows to reach for the Calls desk, where an operator handed a silently truncated list
 * believes they have seen everything.
 */
export const REPLAY_PICKER_OPTION_LIMIT = 50;

/** Marks offered for the nearest-event anchor. Newest first; the scrub just passed them. */
export const REPLAY_MARK_OPTION_LIMIT = 40;

export interface ReplayWindow {
  startMs: number;
  endMs: number;
}

export type ReplayWindowResolution =
  | { status: "ok"; window: ReplayWindow; clamped: boolean }
  | { status: "outside_retention"; earliestMs: number; retentionDays: number };

/** Oldest instant this account may replay. */
export function replayRetentionFloorMs(nowMs: number, retentionDays: number): number {
  return nowMs - retentionDays * DAY_MS;
}

/**
 * Clamp a requested window into the retention calendar.
 *
 * A window that ends before the floor is refused outright: there is nothing inside retention to
 * draw, and drawing an empty tape would say "nobody was here" about a day we simply may not
 * read. A window that merely starts too early is clamped and flagged, so the chrome can say the
 * tape was trimmed rather than silently shortening the operator's day.
 */
export function resolveReplayWindow(
  requested: ReplayWindow,
  options: { nowMs: number; retentionDays?: number },
): ReplayWindowResolution {
  const retentionDays = replayRetentionDays(options.retentionDays);
  const floorMs = replayRetentionFloorMs(options.nowMs, retentionDays);
  const endMs = Math.min(requested.endMs, options.nowMs);
  if (!Number.isFinite(requested.startMs) || !Number.isFinite(requested.endMs) || endMs <= floorMs) {
    return { status: "outside_retention", earliestMs: floorMs, retentionDays };
  }
  const startMs = Math.max(requested.startMs, floorMs);
  if (startMs >= endMs) {
    return { status: "outside_retention", earliestMs: floorMs, retentionDays };
  }
  return {
    status: "ok",
    window: { startMs, endMs },
    // Only a trimmed START counts as clamped. Capping the end at now is not lost history, it is
    // Replay refusing to have a future, and today's date would otherwise always claim a trim.
    clamped: startMs > requested.startMs,
  };
}

/**
 * One LOCAL calendar day starting at the given local-midnight instant.
 *
 * The end is the next local midnight, not a fixed 24 hours. A fall-back day is 25 hours long
 * and a spring-forward day 23, and the host cuts the day index by local calendar day. A fixed
 * span would light a fall-back day whose 25th hour Console never read, and would draw the first
 * hour of the following day under a spring-forward date. The free-duty history reader accepts
 * up to 25 hours for exactly this reason.
 */
export function replayDayWindow(dayStartMs: number): ReplayWindow {
  const end = new Date(dayStartMs);
  end.setDate(end.getDate() + 1);
  const endMs = end.getTime();
  return {
    startMs: dayStartMs,
    endMs: Number.isFinite(endMs) && endMs > dayStartMs ? endMs : dayStartMs + DAY_MS,
  };
}

function pingMillis(ping: DutyPing): number {
  const iso = ping.serverReceivedAt ?? ping.deviceTime;
  if (iso === null || iso.length === 0) return Number.NEGATIVE_INFINITY;
  const ms = Date.parse(iso);
  return Number.isFinite(ms) ? ms : Number.NEGATIVE_INFINITY;
}

function plottable(ping: DutyPing): boolean {
  return (
    ping.lat !== null &&
    ping.lng !== null &&
    Number.isFinite(ping.lat) &&
    Number.isFinite(ping.lng) &&
    ping.lat >= -90 &&
    ping.lat <= 90 &&
    ping.lng >= -180 &&
    ping.lng <= 180
  );
}

/**
 * Plottable samples inside the window, oldest first, capped to the tape budget.
 *
 * The timestamp is parsed ONCE per sample and carried through the sort rather than re-parsed
 * inside the comparator. Same order, same output; a comparator that re-parses turns a windowed
 * site day into tens of thousands of Date.parse calls, and this runs behind a scrub head.
 */
export function replayTapeSamples(
  pings: readonly DutyPing[],
  window: ReplayWindow,
): DutyPing[] {
  const dated: { at: number; ping: DutyPing }[] = [];
  for (const ping of pings) {
    if (!plottable(ping)) continue;
    const at = pingMillis(ping);
    if (at < window.startMs || at > window.endMs) continue;
    dated.push({ at, ping });
  }
  dated.sort((left, right) => left.at - right.at);
  return dated.slice(-REPLAY_TAPE_POINT_LIMIT).map((row) => row.ping);
}

/**
 * Even-stride thinning for the scrubber. First and last samples always survive, so the tape
 * still starts and ends where the officer actually did. Order is preserved; nothing is averaged
 * into a synthetic point.
 */
export function downsampleReplaySamples<T>(
  samples: readonly T[],
  maxSamples: number = REPLAY_SLIDER_MAX_SAMPLES,
): T[] {
  const cap = Math.max(2, Math.floor(maxSamples));
  if (samples.length <= cap) return [...samples];
  const stride = (samples.length - 1) / (cap - 1);
  const kept: T[] = [];
  for (let index = 0; index < cap; index += 1) {
    const source = samples[Math.round(index * stride)];
    if (source !== undefined) kept.push(source);
  }
  const last = samples[samples.length - 1];
  if (last !== undefined && kept[kept.length - 1] !== last) kept[kept.length - 1] = last;
  return kept;
}

/** Samples at or before the playhead. The tape draws up to now, never past it. */
export function replaySamplesThrough(
  samples: readonly DutyPing[],
  playheadMs: number,
): DutyPing[] {
  return samples.filter((ping) => pingMillis(ping) <= playheadMs);
}

/**
 * Where the subject was at the scrub time: the last real sample at or before it. Null before
 * the first ping in the window, because "not reported yet" is an answer and a guessed
 * coordinate is not.
 */
export function replayPositionAt(
  samples: readonly DutyPing[],
  playheadMs: number,
): { lng: number; lat: number; atMs: number } | null {
  let found: DutyPing | null = null;
  for (const ping of samples) {
    if (pingMillis(ping) <= playheadMs) found = ping;
    else break;
  }
  if (found === null || found.lat === null || found.lng === null) return null;
  return { lng: found.lng, lat: found.lat, atMs: pingMillis(found) };
}

/** Events inside the window and at or before the playhead. Never an invented coordinate. */
export function replayEventsThrough(
  events: readonly CommandFocusEventInput[],
  window: ReplayWindow,
  playheadMs: number,
): CommandFocusEvent[] {
  const ceiling = Math.min(playheadMs, window.endMs);
  return events
    .flatMap((input) => {
      const event = commandFocusEvent(input);
      if (event === null || event.occurredAt === null) return [];
      const at = Date.parse(event.occurredAt);
      if (!Number.isFinite(at) || at < window.startMs || at > ceiling) return [];
      return [event];
    })
    .sort((left, right) => Date.parse(left.occurredAt ?? "") - Date.parse(right.occurredAt ?? ""));
}

// --- Picker option builders -----------------------------------------------------------------

/**
 * One picker row per retained person identity.
 *
 * The host has already grouped retained pings by the stable redacted guard ref and supplied the
 * full assignment union. Different people may share a display name, so only those collisions gain
 * the shortest unique ref prefix. Route and site are deliberately absent from person labels.
 */
export function replayOfficerOptions(
  rows: readonly ReplayOfficerSubject[],
): ReplayOfficerOption[] {
  const byRef = new Map<string, ReplayOfficerSubject>();
  for (const row of rows) {
    if (byRef.has(row.guardRef)) continue;
    byRef.set(row.guardRef, row);
  }
  const labelCounts = new Map<string, number>();
  for (const row of byRef.values()) {
    labelCounts.set(row.guardLabel, (labelCounts.get(row.guardLabel) ?? 0) + 1);
  }

  const qualifierFor = (row: ReplayOfficerSubject): string => {
    const peers = [...byRef.values()].filter(
      (candidate) => candidate.guardLabel === row.guardLabel && candidate.guardRef !== row.guardRef,
    );
    for (let length = 4; length <= row.guardRef.length; length += 1) {
      const prefix = row.guardRef.slice(0, length);
      if (peers.every((candidate) => candidate.guardRef.slice(0, length) !== prefix)) return prefix;
    }
    return row.guardRef;
  };

  const options: ReplayOfficerOption[] = [];
  for (const row of byRef.values()) {
    const ambiguous = (labelCounts.get(row.guardLabel) ?? 0) > 1;
    options.push({
      guardRef: row.guardRef,
      routeAssignmentIds: row.routeAssignmentIds,
      label: ambiguous ? `${row.guardLabel} · ${qualifierFor(row)}` : row.guardLabel,
    });
  }
  options.sort(
    (left, right) =>
      left.label.localeCompare(right.label) ||
      left.guardRef.localeCompare(right.guardRef),
  );
  // Deliberately UNCAPPED. This function answers "who may be offered", which is a policy
  // question; how many fit in a dropdown is a presentation question, and the caller that applies
  // the cap is the one that can also print what it hid.
  return options;
}

const REPLAY_OFFICER_SUBJECT_KEYS = [
  "guard_label",
  "guard_ref",
  "route_assignment_ids",
] as const;
const REPLAY_GUARD_REF_PATTERN = /^[0-9a-f]{8}$/i;
const REPLAY_UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const RAW_UUID_PATTERN =
  /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;

/** Strict boundary mapper for the coordinate-free retained Officer subject contract. */
export function mapReplayOfficerSubject(raw: unknown): ReplayOfficerSubject | null {
  if (typeof raw !== "object" || raw === null || Array.isArray(raw)) return null;
  const row = raw as Record<string, unknown>;
  const keys = Object.keys(row).sort();
  if (
    keys.length !== REPLAY_OFFICER_SUBJECT_KEYS.length ||
    keys.some((key, index) => key !== REPLAY_OFFICER_SUBJECT_KEYS[index])
  ) return null;

  const guardRef = typeof row.guard_ref === "string" ? row.guard_ref.toLowerCase() : "";
  const rawLabel = typeof row.guard_label === "string" ? row.guard_label.trim() : "";
  if (!REPLAY_GUARD_REF_PATTERN.test(guardRef) || !Array.isArray(row.route_assignment_ids)) {
    return null;
  }
  if (
    row.route_assignment_ids.length === 0 ||
    row.route_assignment_ids.length > REPLAY_DAY_INDEX_MAX_ASSIGNMENTS
  ) return null;
  const routeAssignmentIds: string[] = [];
  const seenAssignments = new Set<string>();
  for (const value of row.route_assignment_ids) {
    if (typeof value !== "string" || !REPLAY_UUID_PATTERN.test(value)) return null;
    const normalized = value.toLowerCase();
    if (seenAssignments.has(normalized)) continue;
    seenAssignments.add(normalized);
    routeAssignmentIds.push(normalized);
  }
  if (routeAssignmentIds.length === 0) return null;
  routeAssignmentIds.sort();

  const safeLabel =
    rawLabel.length > 0 && !RAW_UUID_PATTERN.test(rawLabel) && !/^[0-9a-f]{32}$/i.test(rawLabel)
      ? rawLabel
      : `Guard ${guardRef}`;
  return { guardRef, guardLabel: safeLabel, routeAssignmentIds };
}

/** Retained people with assigned pings, grouped by stable redacted person ref at the host. */
export async function fetchReplayOfficerSubjects(
  client: SupabaseClient,
  accountId: string,
): Promise<ReplayOfficerSubject[]> {
  const { data, error } = await client.rpc("list_replay_officer_subjects", {
    active_account_id: accountId,
  });
  if (error !== null || !Array.isArray(data) || data.length > REPLAY_DAY_INDEX_MAX_ASSIGNMENTS) {
    throw new Error(REPLAY_OFFICERS_FAILED);
  }
  const subjects: ReplayOfficerSubject[] = [];
  const seenRefs = new Set<string>();
  for (const raw of data) {
    const subject = mapReplayOfficerSubject(raw);
    if (subject === null || seenRefs.has(subject.guardRef)) {
      throw new Error(REPLAY_OFFICERS_FAILED);
    }
    seenRefs.add(subject.guardRef);
    subjects.push(subject);
  }
  return subjects;
}

/**
 * Calls this desk can honestly offer as a Replay subject.
 *
 * A Call with no `call_ref` is DROPPED, not shown by its uuid. The ref is the number the Calls
 * desk prints, the number on the radio, and the number an operator would later cite; a uuid is
 * none of those, and a picker entry nobody can read back is worse than one fewer entry.
 *
 * Newest activity first, because Replay is after-action and the Call an operator wants is nearly
 * always one of the last few. Ordering is by `updatedAt`, with the ref breaking ties so the same
 * feed always builds the same list.
 */
export function replayCallOptions(calls: readonly ReplayCallSource[]): ReplayCallOption[] {
  const seen = new Set<string>();
  const rows: { option: ReplayCallOption; atMs: number }[] = [];
  for (const call of calls) {
    const callRef = call.callRef;
    if (call.callId.length === 0 || callRef === null || callRef.length === 0) continue;
    if (seen.has(call.callId)) continue;
    seen.add(call.callId);
    const site = call.site ?? null;
    const parsed = Date.parse(call.updatedAt ?? "");
    rows.push({
      option: {
        callId: call.callId,
        callRef,
        label: site === null ? `Call ${callRef}` : `Call ${callRef} · ${site}`,
      },
      atMs: Number.isFinite(parsed) ? parsed : Number.NEGATIVE_INFINITY,
    });
  }
  rows.sort((left, right) => right.atMs - left.atMs || left.option.callRef.localeCompare(right.option.callRef));
  // Uncapped, same reason as the officer list above.
  return rows.map((row) => row.option);
}

/**
 * `+N not listed` for a capped picker, or null when the whole list fits.
 *
 * Same sentence shape as the twelve-tape remainder, for the same reason: a cap an operator cannot
 * see is a cap that lies about how much they have looked at.
 */
export function replayPickerRemainderNote(
  total: number,
  shown: number,
  noun: string,
): string | null {
  const hidden = total - shown;
  return hidden > 0 ? `+${hidden} more ${noun} not listed.` : null;
}

export interface ReplayMarkOption {
  eventId: string;
  kind: CommandFocusEvent["kind"];
  label: string;
  /** Milliseconds, for the panel's own clock formatter. Never a coordinate. */
  atMs: number;
}

/**
 * Marks the scrub has reached, as coordinate-free picker options.
 *
 * `CommandFocusEvent` carries `lat` and `lng` because the map needs them. This strips both before
 * anything reaches the panel: the picker is chrome, chrome prints what it is handed, and the
 * cheapest way to guarantee no coordinate is ever printed is to not hand it one. `atMs` is a
 * number the panel formats with its own clock, not a position.
 *
 * Newest first and capped, because the mark an operator wants to anchor on is the one they just
 * scrubbed past.
 */
export function replayMarkOptions(
  events: readonly CommandFocusEventInput[],
  window: ReplayWindow,
  playheadMs: number,
): ReplayMarkOption[] {
  const reached = replayEventsThrough(events, window, playheadMs);
  const options = reached.map((event) => ({
    eventId: event.id,
    kind: event.kind,
    label: event.label,
    atMs: Date.parse(event.occurredAt ?? ""),
  }));
  // `replayEventsThrough` sorts oldest first; the picker wants the other end. Uncapped, same
  // reason as the subject lists: the caller caps and prints what it hid.
  options.reverse();
  return options;
}

// --- Nearest-event ranking ------------------------------------------------------------------
// Lock session 2: "SOS or subject officer first, then nearest the event point, then ping count."
// The middle tier needs an EVENT POINT, and for a long time there was none, so `planReplayTapes`
// was handed a hardcoded null and the tier quietly did nothing. Everything below exists to make
// that tier answerable from what the desk already knows, and to leave it honestly null when the
// desk knows nothing.

/** The point a Replay window is anchored on, when the desk knows one. */
export interface ReplayEventAnchor {
  eventId: string;
  lng: number;
  lat: number;
}

function eventMillis(event: CommandFocusEvent): number {
  const at = Date.parse(event.occurredAt ?? "");
  // Undated events sort last rather than first. An event with no time is not the earliest one.
  return Number.isFinite(at) ? at : Number.POSITIVE_INFINITY;
}

/** Oldest event in a set, id breaking ties so the same window always picks the same anchor. */
function earliestEvent(events: readonly CommandFocusEvent[]): CommandFocusEvent | null {
  let best: CommandFocusEvent | null = null;
  let bestAt = Number.POSITIVE_INFINITY;
  for (const event of events) {
    const at = eventMillis(event);
    if (best === null || at < bestAt || (at === bestAt && event.id.localeCompare(best.id) < 0)) {
      best = event;
      bestAt = at;
    }
  }
  return best;
}

/**
 * The event this Replay is anchored on, in the order the desk actually knows one.
 *
 * 1. The operator's own pick. An explicitly selected tick is the event, full stop.
 * 2. A Call subject's first located rung. Replaying a Call means replaying the place it happened,
 *    and the earliest rung carrying a fix is the closest thing the ladder has to that place.
 * 3. The window's first SOS. On the night an SOS fired, "who was nearest it" is the question the
 *    middle tier exists to answer.
 *
 * All three tiers now reach a live desk. Tier 1 arrives from the Mark picker on
 * `ReplayDeskPanel`, which lists the marks the scrub has reached and hands the chosen one back as
 * `selectedEventId`; the `TODO(replay-w3)` that stood here while no caller could supply it is
 * discharged, and `command-replay.test.ts` pins the tier against a real rank change.
 *
 * Resolved over the WHOLE window rather than over the events the scrub has reached. The anchor is
 * a property of the day, not of the playhead: an anchor that appeared mid-scrub would re-rank the
 * twelve tapes and, because identity colour is draw order, repaint them while an operator watched.
 *
 * Returns null when none of the three hold, which is the honest answer for a plain site day and
 * the case that leaves `distanceToEventMeters` null.
 */
export function replayEventAnchor(
  events: readonly CommandFocusEvent[],
  options: {
    /** The mark the operator picked on the desk, when they picked one. */
    selectedEventId?: string | null;
    callId?: string | null;
  } = {},
): ReplayEventAnchor | null {
  const anchor = (event: CommandFocusEvent): ReplayEventAnchor => ({
    eventId: event.id,
    lng: event.lng,
    lat: event.lat,
  });

  const selectedId = options.selectedEventId ?? null;
  if (selectedId !== null) {
    const picked = events.find((event) => event.id === selectedId);
    if (picked !== undefined) return anchor(picked);
  }

  const callId = options.callId ?? null;
  if (callId !== null) {
    // Ladder ids are `call:{callId}:{seq}`, and `fetchReplayEvents` only emits a rung that
    // carried GPS proof, so any match here is a real reported coordinate.
    const prefix = `call:${callId}:`;
    const rung = earliestEvent(
      events.filter((event) => event.kind === "ladder" && event.id.startsWith(prefix)),
    );
    if (rung !== null) return anchor(rung);
  }

  const sos = earliestEvent(events.filter((event) => event.kind === "sos"));
  return sos === null ? null : anchor(sos);
}

const EARTH_RADIUS_METERS = 6_371_000;

function radians(value: number): number {
  return (value * Math.PI) / 180;
}

function haversineMeters(latA: number, lngA: number, latB: number, lngB: number): number {
  const dLat = radians(latB - latA);
  const dLng = radians(lngB - lngA);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(radians(latA)) * Math.cos(radians(latB)) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_METERS * Math.asin(Math.sqrt(a));
}

/**
 * How close this tape ever came to the anchor inside the window.
 *
 * Closest approach, not the newest sample and not the sample under the playhead. "Nearest the
 * event" is a question about the whole tape: an officer who stood on the SOS at 02:00 and drove
 * away by 06:00 was nearer it than one who never left the far gate, and ranking on either end
 * sample would say the opposite. It is also the only one of the three that does not move as the
 * scrub head moves, which keeps the twelve drawn tapes and their identity colours steady through
 * a playback.
 *
 * Measured against the DOWNSAMPLED samples the tape actually holds, so it is an approximation of
 * the true closest approach, thinned the same way the drawn tape is. Null when the tape has no
 * plottable sample or the desk has no anchor.
 *
 * Runs once per tape per committed frame, bounded by the same REPLAY_SLIDER_MAX_SAMPLES budget
 * that bounds everything else on this desk, and strictly cheaper than the gradient rebuild the
 * drawn tapes already pay for on the same frame.
 */
export function replayClosestApproachMeters(
  samples: readonly DutyPing[],
  anchor: ReplayEventAnchor | null,
): number | null {
  if (anchor === null) return null;
  let best: number | null = null;
  for (const ping of samples) {
    if (!plottable(ping) || ping.lat === null || ping.lng === null) continue;
    const meters = haversineMeters(ping.lat, ping.lng, anchor.lat, anchor.lng);
    if (best === null || meters < best) best = meters;
  }
  return best;
}

export interface ReplayTapeCandidate {
  /** Route assignment id, or the free-duty marker id for an Unassigned officer. */
  id: string;
  /** The officer this Replay is about, when the subject names one. */
  isSubject: boolean;
  /** Any SOS on this tape inside the window. */
  hasSos: boolean;
  /** Metres from the event the Replay is anchored on, when there is one. */
  distanceToEventMeters: number | null;
  /** Samples this officer actually has in the window. More samples is a fuller story. */
  pingCount: number;
}

/**
 * Session 2 site Replay cap: at most 12 tapes, remainder named rather than dropped.
 *
 * Rank order is the lock's: the subject or anyone with an SOS first, then whoever was nearest
 * the event, then whoever has the most pings. Ties fall back to id so the same window always
 * draws the same twelve officers; a cap that reshuffled between renders would let an operator
 * believe a tape vanished.
 */
export function planReplayTapes(candidates: readonly ReplayTapeCandidate[]): LiveTrailPlan {
  const ordered = [...candidates].sort((left, right) => {
    const leftPriority = left.isSubject || left.hasSos ? 0 : 1;
    const rightPriority = right.isSubject || right.hasSos ? 0 : 1;
    if (leftPriority !== rightPriority) return leftPriority - rightPriority;
    const leftDistance = left.distanceToEventMeters ?? Number.POSITIVE_INFINITY;
    const rightDistance = right.distanceToEventMeters ?? Number.POSITIVE_INFINITY;
    if (leftDistance !== rightDistance) return leftDistance - rightDistance;
    if (left.pingCount !== right.pingCount) return right.pingCount - left.pingCount;
    return left.id.localeCompare(right.id);
  });
  // Cap and remainder copy come from the SAME seam Live uses, so the twelve-tape promise and
  // the "+N officers not drawn" sentence cannot drift between the two desks.
  return planLiveTrails(ordered.map((candidate, index) => ({ id: candidate.id, rank: index })));
}

// --- Overlay assembly -----------------------------------------------------------------------
// Everything the map needs for one scrub frame, built here so the surface stays a thin caller
// and the twelve-tape cap, the identity palette, and the playhead rule are all unit-testable
// without a WebGL context.

export interface ReplayTapeInput {
  /** Route assignment id, or the free-duty marker id for an Unassigned officer. */
  id: string;
  label: string;
  /** Plottable samples already filtered to the window, oldest first. */
  samples: readonly DutyPing[];
}

export interface ReplayOverlay {
  trails: CommandFocusTrailCollection;
  ticks: PointFeatureCollection;
  playhead: PointFeatureCollection;
  drawnIds: string[];
  remainderIds: string[];
  remainderNote: string | null;
  /** The point the nearest-event ranking tier measured against, or null when there was none. */
  eventAnchor: ReplayEventAnchor | null;
}

/** Stable identity colour for the nth drawn tape. Never red, never SOS coral. */
export function replayTapeColor(index: number): string {
  const palette = MAP_THEME.replayIdentityTrail;
  return palette[index % palette.length] ?? MAP_THEME.dutyTrailLineFocus;
}

/**
 * One scrub frame: capped tapes drawn up to the playhead, ticks the scrub has reached, and one
 * playhead mark per drawn tape.
 *
 * Ranking inputs are derived here rather than asked for, so a caller cannot accidentally rank a
 * site tape by something the lock does not name. SOS presence comes from the events themselves.
 */
export function buildReplayOverlay(options: {
  tapes: readonly ReplayTapeInput[];
  events: readonly CommandFocusEventInput[];
  window: ReplayWindow;
  playheadMs: number;
  /** The officer this Replay is about, when the subject names one. */
  subjectId?: string | null;
  selectedEventId?: string | null;
  /** The Call this Replay is about, when the subject names one. Anchors the nearest-event tier. */
  callId?: string | null;
}): ReplayOverlay {
  const { tapes, events, window, playheadMs } = options;
  const subjectId = options.subjectId ?? null;
  // Every event in the window, resolved once. `reached` is the prefix the scrub has arrived at;
  // the ranking anchor below deliberately reads the whole window instead.
  const windowEvents = replayEventsThrough(events, window, window.endMs);
  const ceiling = Math.min(playheadMs, window.endMs);
  const reached = windowEvents.filter((event) => Date.parse(event.occurredAt ?? "") <= ceiling);
  const sosTapeIds = new Set(
    reached.filter((event) => event.kind === "sos").map((event) => event.id.split(":")[1] ?? ""),
  );
  const eventAnchor = replayEventAnchor(windowEvents, {
    selectedEventId: options.selectedEventId ?? null,
    callId: options.callId ?? null,
  });

  const plan = planReplayTapes(
    tapes.map((tape) => ({
      id: tape.id,
      isSubject: subjectId !== null && tape.id === subjectId,
      // An SOS id is `sos:{alertEventId}`, which is not a tape id, so a tape only counts as an
      // SOS tape when the event id genuinely names it. Guessing here would promote the wrong
      // officer into the twelve.
      hasSos: sosTapeIds.has(tape.id),
      // Null when the desk has no anchor, which leaves the middle tier inert and rank falling
      // through to ping count exactly as it did before. That is the honest shape for a plain
      // site day: there is no event to be near.
      distanceToEventMeters: replayClosestApproachMeters(tape.samples, eventAnchor),
      pingCount: tape.samples.length,
    })),
  );

  const drawn = plan.drawnIds
    .map((id) => tapes.find((tape) => tape.id === id))
    .filter((tape): tape is ReplayTapeInput => tape !== undefined);

  const trailFeatures: CommandFocusTrailCollection["features"] = [];
  const playheadFeatures: PointFeatureCollection["features"] = [];

  drawn.forEach((tape, index) => {
    const color = replayTapeColor(index);
    const through = replaySamplesThrough(tape.samples, playheadMs);
    const gradient = toGradientTrailFeatureCollection(through, tape.id, `${tape.label} tape`, {
      startMs: window.startMs,
      // Strongest segment sits at the scrub head, which is what makes this read as radar
      // rather than as a static line that happens to be short.
      endMs: Math.max(window.startMs + 1, Math.min(playheadMs, window.endMs)),
    });
    for (const feature of gradient.features) {
      trailFeatures.push({
        ...feature,
        properties: { ...feature.properties, color },
      });
    }
    const position = replayPositionAt(through, playheadMs);
    if (position !== null) {
      playheadFeatures.push({
        type: "Feature",
        geometry: { type: "Point", coordinates: [position.lng, position.lat] },
        properties: {
          id: tape.id,
          name: tape.label,
          guardLabel: tape.label,
          color,
          layerLabel: "Replay position",
        },
      });
    }
  });

  return {
    trails: { type: "FeatureCollection", features: trailFeatures },
    ticks: toCommandFocusTickFeatureCollection(reached, options.selectedEventId ?? null),
    playhead: { type: "FeatureCollection", features: playheadFeatures },
    drawnIds: plan.drawnIds,
    remainderIds: plan.remainderIds,
    remainderNote: plan.remainderNote,
    eventAnchor,
  };
}

// --- Clock ----------------------------------------------------------------------------------
// Speed is expressed against the WINDOW, not against real time. At 1x the whole selected window
// plays in REPLAY_PLAYBACK_BASE_SECONDS, so a Call that lasted nine minutes and a full shift day
// both scrub at a predictable pace and an operator does not have to do arithmetic to guess how
// long a tape will take.

export const REPLAY_PLAYBACK_BASE_SECONDS = 60;
export const REPLAY_SPEEDS: readonly number[] = [1, 2, 4, 8];

/**
 * Smallest real-time gap between two COMMITTED playhead moves.
 *
 * Every commit re-slices up to twelve tapes at the new playhead, rebuilds the gradient, and makes
 * the map drop and re-add every entity on the desk. Committing once per display frame does that
 * work about sixty times a second over a structure that can hold six thousand samples, which is
 * enough to take an operator's tab down on a busy site day. Ten commits a second still reads as
 * motion, and a scrub that cost the operator their desk is not a feature.
 *
 * This is a rendering budget, not a clock: no window time is dropped, because the elapsed span is
 * accumulated and spent whole on the next commit.
 */
export const REPLAY_TICK_MIN_MS = 100;

export interface ReplayClockState {
  playheadMs: number;
  playing: boolean;
  speed: number;
}

/** Snap a requested speed onto the offered set, failing safe to 1x. */
export function replaySpeed(requested: number | null | undefined): number {
  return typeof requested === "number" && REPLAY_SPEEDS.includes(requested) ? requested : 1;
}

/** Put the playhead inside the window. A scrub can never run past now or before the start. */
export function clampPlayhead(playheadMs: number, window: ReplayWindow): number {
  if (!Number.isFinite(playheadMs)) return window.startMs;
  return Math.min(window.endMs, Math.max(window.startMs, playheadMs));
}

/**
 * Advance the clock by real elapsed milliseconds. Reaching the end pauses rather than looping,
 * because a tape that silently restarted would let an operator read the beginning of a shift as
 * the end of one.
 */
export function advanceReplayClock(
  state: ReplayClockState,
  options: { elapsedMs: number; window: ReplayWindow },
): ReplayClockState {
  const { elapsedMs, window } = options;
  if (!state.playing || elapsedMs <= 0) {
    return { ...state, playheadMs: clampPlayhead(state.playheadMs, window) };
  }
  const windowMs = Math.max(1, window.endMs - window.startMs);
  const perRealMs = windowMs / (REPLAY_PLAYBACK_BASE_SECONDS * 1000);
  const next = state.playheadMs + elapsedMs * perRealMs * replaySpeed(state.speed);
  if (next >= window.endMs) {
    return { playheadMs: window.endMs, playing: false, speed: replaySpeed(state.speed) };
  }
  return { playheadMs: clampPlayhead(next, window), playing: true, speed: replaySpeed(state.speed) };
}

// --- Reads ----------------------------------------------------------------------------------
//
// ONE CLOCK. A Replay desk session captures `now` when it opens and hands that same instant to
// every helper below. It used to be two: the panel resolved its window against a frozen open-time
// clock while these reads re-clamped against a live `Date.now()`. At the retention floor those two
// disagree, and the disagreement was silent — the panel drew a normal scrubber over a window the
// read had refused and returned `[]` for, which paints as "nobody was there" for a day we simply
// were no longer allowed to read.
//
// The live clock was not paranoia, though. It existed so a desk left open for a week could not
// walk its own retention floor backwards a day at a time. That guarantee is kept here instead as a
// freshness bound on the clock these reads are handed: a desk clock older than
// REPLAY_DESK_CLOCK_MAX_AGE_MS is refused rather than trusted, and the caller recaptures. So the
// window and the read still agree, and the retention promise still cannot be stretched by leaving
// a tab open.

/**
 * How far the desk clock may lag real time before a Replay read stops trusting it.
 *
 * This bounds how far past the retention floor a long-open desk could reach: fifteen minutes on a
 * promise measured in weeks. Short enough that the overshoot is meaningless, long enough that an
 * operator reading a tape is never interrupted mid-sitting.
 */
export const REPLAY_DESK_CLOCK_MAX_AGE_MS = 15 * 60 * 1000;

/**
 * Whether a desk clock is fresh enough for a Replay read to honour it.
 *
 * The ONE definition of that rule. `guardReplayRead` asks it before serving a window, and the
 * desk's heal asks the same question before replacing a clock a read just refused. Two copies of
 * this predicate is how the desk ends up holding a clock every read rejects with no state change
 * to re-run them, which paints an empty tape for a window we refused — a second spelling of the
 * two-clock bug this whole section exists to close.
 *
 * Only a LAGGING clock fails. A device whose clock runs fast hands over a `now` in the future,
 * which pushes the retention floor forward and reads LESS history, never more. A non-finite value
 * is never usable: it is not a clock.
 *
 * `atMs` is the instant to measure against, threaded in so a caller that already read the clock
 * does not take a second, differing reading.
 */
export function replayDeskClockUsable(nowMs: number, atMs: number = Date.now()): boolean {
  return Number.isFinite(nowMs) && atMs - nowMs <= REPLAY_DESK_CLOCK_MAX_AGE_MS;
}

/**
 * Why a Replay read declined, when it did.
 *
 * A refusal is NOT an empty tape, and the two must not share a shape. `outside_retention` means
 * the window is not ours to read; `stale_desk_clock` means the desk session's `now` has aged far
 * enough that honouring it would quietly widen retention. Both want the surface to say something
 * other than "no pings".
 */
export type ReplayReadRefusal =
  | { status: "outside_retention"; earliestMs: number; retentionDays: number }
  | { status: "stale_desk_clock"; deskClockAgeMs: number };

/**
 * A stored duty ping with optional Replay-only presentation identity.
 *
 * Assigned subjects continue to use `routeAssignmentId`. Free-duty history has no assignment by
 * definition, so its redacted member ref is carried separately and never made to masquerade as a
 * route assignment. Neither field can contain the underlying auth user id: the hosted RPC does
 * not return it and the mapper below accepts only the eight-character redacted ref.
 */
export interface ReplayPing extends DutyPing {
  replayTapeId?: string;
  replayTapeLabel?: string;
}

export type ReplayPingRead = { status: "ok"; pings: ReplayPing[] } | ReplayReadRefusal;

export type ReplayEventRead = { status: "ok"; events: CommandFocusEvent[] } | ReplayReadRefusal;

/**
 * Copy for a day that aged out of retention while the desk was open.
 *
 * Deliberately distinct from the picker's "that day is outside this account's Replay window".
 * One is a day the operator chose and cannot have; the other is a day they were already reading
 * when it left the calendar, and saying so is the difference between a product with a rule and a
 * product that appeared to lose a tape.
 */
export const REPLAY_RETENTION_LAPSED =
  "That day left this account's Replay window while the desk was open. Pick a more recent day.";

/**
 * Shared gate for both reads: clamp the caller's window against the DESK clock it was handed, and
 * refuse a desk clock that has gone stale.
 *
 * Only a LAGGING clock is refused. A device whose clock runs fast hands over a `now` in the
 * future, which pushes the retention floor forward and reads less history, never more — narrower
 * than policy is a shortfall, not a leak, and the desk stays internally consistent because the
 * panel resolved its window against the same value.
 */
function guardReplayRead(
  window: ReplayWindow,
  nowMs: number,
): { status: "ok"; window: ReplayWindow } | ReplayReadRefusal {
  // The only place these functions read a clock of their own, and it is not used to build a
  // window — only to ask whether the window's clock is still fresh enough to believe. Read ONCE
  // and passed to the predicate, so the verdict and the age reported with it agree.
  const atMs = Date.now();
  const deskClockAgeMs = atMs - nowMs;
  if (!replayDeskClockUsable(nowMs, atMs)) {
    return { status: "stale_desk_clock", deskClockAgeMs };
  }
  const bounded = resolveReplayWindow(window, {
    nowMs,
    // The WIDEST chip lock Q12 names. The narrower per-account chip is resolved upstream by the
    // desk's own `resolveReplayWindow`, so this is the backstop, not the product rule.
    retentionDays: Math.max(...REPLAY_RETENTION_ALLOWED_DAYS),
  });
  return bounded.status === "ok"
    ? { status: "ok", window: bounded.window }
    : { status: "outside_retention", earliestMs: bounded.earliestMs, retentionDays: bounded.retentionDays };
}

/**
 * Windowed duty pings for a Replay subject.
 *
 * Assigned officers/sites and Calls use the RLS-protected duty_location_pings projection.
 * Unassigned uses the hosted `list_free_duty_replay_pings` RPC: null-assignment history can only
 * be separated by user_id in storage, and that identifier must never cross into Console. The RPC
 * replaces it with a stable redacted member ref and applies the same account/audience/policy
 * boundary server-side. This read never calls the Live presence RPC or reconstructs a day from a
 * current-state snapshot.
 *
 * This function re-clamps the window it was handed instead of trusting it. The assigned-subject
 * SELECT policy has no time bound, so a caller passing a raw picker value there could otherwise
 * turn "thirty days" into "everything ever recorded". Unassigned receives this same Console
 * guard and the hosted RPC defensively applies the account retention value again.
 *
 * `nowMs` is REQUIRED and is the desk session's clock, the same instant the panel resolved its
 * window against. There is no default, because a default would be a second clock, and a second
 * clock is the whole of the bug this signature exists to close. A refusal comes back as a status
 * rather than an empty list for the same reason: an empty tape says nobody was there.
 */
export async function fetchReplayPings(
  client: SupabaseClient,
  accountId: string,
  options: {
    window: ReplayWindow;
    /** The desk session's captured clock. Same instant the panel resolved its window against. */
    nowMs: number;
    routeAssignmentIds?: readonly string[];
    callId?: string | null;
    /** Redacted free-duty history. Mutually exclusive with assignment and Call scope. */
    unassigned?: boolean;
  },
): Promise<ReplayPingRead> {
  const guarded = guardReplayRead(options.window, options.nowMs);
  if (guarded.status !== "ok") {
    return guarded;
  }
  const replayWindow = guarded.window;
  if (options.unassigned === true) {
    const { data, error } = await client.rpc("list_free_duty_replay_pings", {
      active_account_id: accountId,
      p_window_start: new Date(replayWindow.startMs).toISOString(),
      p_window_end: new Date(replayWindow.endMs).toISOString(),
    });
    if (error !== null) {
      throw new Error("Could not load the Replay tape.");
    }
    if (!Array.isArray(data)) throw new Error("Could not load the Replay tape.");
    const pings: ReplayPing[] = [];
    for (const raw of data) {
      if (typeof raw !== "object" || raw === null || Array.isArray(raw)) {
        throw new Error("Could not load the Replay tape.");
      }
      const ping = mapFreeDutyReplayPing(raw);
      // A malformed identity row is UNKNOWN, not an absent officer. Refuse the whole tape so a
      // broken/redaction-regressed RPC response cannot be relabelled as an honestly empty day.
      if (ping === null) throw new Error("Could not load the Replay tape.");
      const receivedAtMs = Date.parse(ping.serverReceivedAt ?? "");
      if (receivedAtMs < replayWindow.startMs || receivedAtMs > replayWindow.endMs) {
        throw new Error("Could not load the Replay tape.");
      }
      pings.push(ping);
    }
    return { status: "ok", pings };
  }
  const assignmentIds = options.routeAssignmentIds ?? [];
  const callId = options.callId ?? null;
  if (callId === null && assignmentIds.length === 0) {
    return { status: "ok", pings: [] };
  }
  let query = client
    .from("duty_location_pings")
    .select(DUTY_PING_COLUMNS)
    .eq("account_id", accountId)
    .gte("server_received_at", new Date(replayWindow.startMs).toISOString())
    .lte("server_received_at", new Date(replayWindow.endMs).toISOString());
  query = callId !== null
    ? query.eq("duty_call_id", callId)
    : query.in("route_assignment_id", [...assignmentIds]);
  const { data, error } = await query
    .order("server_received_at", { ascending: true })
    .limit(REPLAY_TAPE_POINT_LIMIT * LIVE_TRAIL_PAINT_CAP);
  if (error !== null) {
    throw new Error("Could not load the Replay tape.");
  }
  if (!Array.isArray(data)) {
    return { status: "ok", pings: [] };
  }
  return { status: "ok", pings: data.map((row) => mapDutyPing(row as Record<string, unknown>)) };
}

/**
 * Map one row from the redacted free-duty history RPC.
 *
 * The eight-hex ref is the same redaction family Live uses. Rejecting every other shape here is a
 * second boundary behind the RPC contract: a raw uuid, blank key, or surprising identifier can
 * never become a GeoJSON entity id or a React key. The route assignment stays null because these
 * pings were recorded without one; `replayTapeId` is presentation identity, not route data.
 */
export function mapFreeDutyReplayPing(raw: Record<string, unknown>): ReplayPing | null {
  const memberRef = asText(raw.member_ref);
  if (memberRef === null || !/^[0-9a-f]{8}$/i.test(memberRef)) return null;
  const guardLabel = asText(raw.guard_label)?.trim() || null;
  const safeLabel =
    guardLabel !== null &&
    !/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i.test(guardLabel) &&
    !/^[0-9a-f]{32}$/i.test(guardLabel.trim())
      ? guardLabel
      : `Guard ${memberRef}`;
  const ping: ReplayPing = {
    ...mapDutyPing({ ...raw, route_assignment_id: null }),
    replayTapeId: freeDutyMarkerId(memberRef.toLowerCase()),
    replayTapeLabel: safeLabel,
  };
  // The hosted contract makes all three non-null. Treat a missing/bogus fix as a broken response,
  // not as an officer whose history was honestly empty after the mapper silently dropped it.
  if (!plottable(ping) || !Number.isFinite(Date.parse(ping.serverReceivedAt ?? ""))) return null;
  return ping;
}

export const REPLAY_NO_UNASSIGNED_PINGS =
  "No recorded free-duty positions in this day window.";

/**
 * Why a subject KIND has nothing to offer.
 *
 * An empty dropdown and a dropdown nobody may read look identical, and a picker that opens onto
 * nothing reads as a broken control rather than as an account with no roster. Each sentence names
 * the shortage and points at the kinds that still work, so the operator's next move is obvious.
 */
export const REPLAY_NO_OFFICERS =
  "No officers with recorded positions in this Replay window. Pick a site, a Call, or Unassigned.";

export const REPLAY_OFFICERS_LOADING = "Finding officers with recorded positions.";

export const REPLAY_OFFICERS_FAILED = "Could not load officers with recorded positions.";

export const REPLAY_NO_CALLS =
  "No Calls this desk can read. Pick a site, an officer, or Unassigned.";

export const REPLAY_NO_SITES =
  "No sites this desk can read. Pick an officer, a Call, or Unassigned.";

/**
 * The Call picker offers RECENT Calls, and says so.
 *
 * `fetchDutyCalls` is itself bounded — the open page and a short tail of cleared ones — so the
 * picker's own `+N more` can only ever report what the picker cut, never what the feed never
 * carried. Lock Q12's headline example is "a Call from 18 days ago", which on a busy account is
 * not in this list at all. Saying so is the difference between a bounded picker and a picker that
 * quietly claims to be the whole record.
 */
export const REPLAY_CALLS_RECENT_ONLY =
  "Recent Calls only. Older Calls are on the Calls desk.";

/**
 * A subject that left its own list while the desk sat open.
 *
 * The retained subject read and the Calls feed are bounded, so a subject picked at the start of a
 * long sitting can genuinely stop being offerable after refresh.
 * Falling back to a site would swap the operator's subject without telling them, and the tape
 * under the scrubber would quietly become somebody else's. Refuse and say so instead.
 */
export const REPLAY_SUBJECT_UNRESOLVED =
  "That subject is no longer on a list this desk can read. Pick another subject.";

/**
 * Prompts for a kind chosen with nobody chosen inside it.
 *
 * The desk does NOT auto-select the first officer or the newest Call. Pressing a tab must not
 * start reading a named person's month of movement; a picker that pre-picks is not a picker.
 */
export const REPLAY_PICK_OFFICER = "Pick an officer to replay.";

export const REPLAY_PICK_CALL = "Pick a Call to replay.";

/** Group windowed pings into per-officer tapes. Unattributed null-assignment rows are dropped. */
export function replayTapesFromPings(
  pings: readonly ReplayPing[],
  window: ReplayWindow,
  labelFor: (routeAssignmentId: string) => string,
): ReplayTapeInput[] {
  const byAssignment = new Map<string, ReplayPing[]>();
  for (const ping of pings) {
    const id = ping.replayTapeId ?? ping.routeAssignmentId;
    if (id === null || id.length === 0) continue;
    const bucket = byAssignment.get(id);
    if (bucket === undefined) byAssignment.set(id, [ping]);
    else bucket.push(ping);
  }
  const tapes: ReplayTapeInput[] = [];
  for (const [id, rows] of byAssignment) {
    const samples = replayTapeSamples(rows, window);
    if (samples.length === 0) continue;
    // Thin for the scrubber here, at the one place tapes are built. Twelve tapes times a full
    // day of samples is what makes a scrub stutter, and the thinning keeps the first and last
    // real sample so the tape still starts and ends where the officer did.
    const replayLabel = rows.find((row) => row.replayTapeLabel !== undefined)?.replayTapeLabel;
    tapes.push({
      id,
      label: replayLabel ?? labelFor(id),
      samples: downsampleReplaySamples(samples),
    });
  }
  return tapes.sort((left, right) => left.id.localeCompare(right.id));
}

// --- Events over the Replay window ----------------------------------------------------------
// Replay cannot borrow Focus's event reads. Those are scoped to ONE route assignment and to the
// fixed 24 hour Focus window, and site Replay has neither: it has up to twelve assignments and
// an arbitrary recorded day. Passing Focus's events into a Replay frame produced an empty tick
// layer on every site tape, which is a whole product requirement silently doing nothing.

function asText(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

function asNum(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim().length > 0) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

const REPLAY_EVENT_LIMIT = 400;

/**
 * Ladder, scan, and punch marks for a Replay window, keyed on the SAME assignment ids the tapes
 * are keyed on. SOS arrives from the alert feed the desk already holds, so it is merged by the
 * caller rather than re-read here.
 *
 * Every read is bounded on both ends of the window. A read bounded only at the bottom would pull
 * today's events onto last Tuesday's tape.
 *
 * Takes the SAME desk clock the ping read takes, for the same reason: ticks and tape must agree
 * about which window they are describing, and a helper that reached for its own `Date.now()` would
 * be a third clock on a desk that is only allowed one.
 */
export async function fetchReplayEvents(
  client: SupabaseClient,
  accountId: string,
  options: {
    window: ReplayWindow;
    /** The desk session's captured clock. Same instant the panel resolved its window against. */
    nowMs: number;
    routeAssignmentIds: readonly string[];
    callIds?: readonly string[];
  },
): Promise<ReplayEventRead> {
  const assignmentIds = [...options.routeAssignmentIds];
  const callIds = [...(options.callIds ?? [])];
  if (assignmentIds.length === 0 && callIds.length === 0) return { status: "ok", events: [] };
  // Self-clamping, same as the ping read. Retention has exactly one enforcement point in this
  // product, and a read that trusted its caller would not be it.
  const guarded = guardReplayRead(options.window, options.nowMs);
  if (guarded.status !== "ok") return guarded;
  const startIso = new Date(guarded.window.startMs).toISOString();
  const endIso = new Date(guarded.window.endMs).toISOString();

  const scanRead =
    assignmentIds.length === 0
      ? Promise.resolve({ data: [] as unknown[], error: null })
      : client
          .from("supervisor_scan_points")
          .select(SCAN_POINT_COLUMNS)
          .eq("account_id", accountId)
          .in("route_assignment_id", assignmentIds)
          .gte("server_received_at", startIso)
          .lte("server_received_at", endIso)
          .order("server_received_at", { ascending: true })
          .limit(REPLAY_EVENT_LIMIT);

  const punchRead =
    assignmentIds.length === 0
      ? Promise.resolve({ data: [] as unknown[], error: null })
      : client
          .from("management_timeclock_log")
          .select("time_punch_id,route_assignment_id,punch_type,server_received_at,device_time,guard_label")
          .eq("account_id", accountId)
          .in("route_assignment_id", assignmentIds)
          .gte("server_received_at", startIso)
          .lte("server_received_at", endIso)
          .order("server_received_at", { ascending: true })
          .limit(REPLAY_EVENT_LIMIT);

  const ladderRead =
    callIds.length === 0
      ? Promise.resolve({ data: [] as unknown[], error: null })
      : client
          .from("duty_call_status_events")
          .select("duty_call_id,event_type,recorded_at,event_seq,lat,lng,gps_captured_at")
          .eq("account_id", accountId)
          .in("duty_call_id", callIds)
          .gte("recorded_at", startIso)
          .lte("recorded_at", endIso)
          .order("recorded_at", { ascending: true })
          .limit(REPLAY_EVENT_LIMIT);

  /**
   * SOS over the SAME window as the other three kinds.
   *
   * Previously SOS came from the desk's live alert feed, which is the fifty most recent alerts
   * and carries no window. That made SOS silently absent from every historical day and left the
   * SOS tier of the twelve-tape ranker inert exactly when an operator is replaying the night an
   * SOS happened. management_alert_queue has no time predicate of its own, only a role gate that
   * already matches the Replay audience, so it can simply be asked for the window.
   */
  const sosRead =
    assignmentIds.length === 0
      ? Promise.resolve({ data: [] as unknown[], error: null })
      : client
          .from("management_alert_queue")
          .select("alert_event_id,alert_type,route_assignment_id,detected_at,duty_lat,duty_lng,guard_label")
          .eq("account_id", accountId)
          .in("route_assignment_id", assignmentIds)
          .gte("detected_at", startIso)
          .lte("detected_at", endIso)
          .order("detected_at", { ascending: true })
          .limit(REPLAY_EVENT_LIMIT);

  // Per source, not all-or-nothing. Ticks are additive and the four kinds are independent, so
  // one dead read must not blank the other three: a punch view that is mid-migration should
  // cost the punch ticks, not every mark on the tape.
  const settled = await Promise.allSettled([scanRead, punchRead, ladderRead, sosRead]);
  const rowsOf = (index: number): unknown[] => {
    const outcome = settled[index];
    if (outcome === undefined || outcome.status !== "fulfilled") return [];
    const { data, error } = outcome.value as { data: unknown; error: unknown };
    return error !== null || !Array.isArray(data) ? [] : data;
  };
  const scanData = rowsOf(0);
  const punchData = rowsOf(1);
  const ladderData = rowsOf(2);
  const sosData = rowsOf(3);

  const events: CommandFocusEventInput[] = [];

  for (const raw of scanData) {
    const point = mapScanPoint(raw as Record<string, unknown>);
    // Only a real device fix earns a tick. A checkpoint-pin fallback is the post's expected
    // position, not where the officer stood.
    if (point === null || point.mapCoordSource !== "scan_gps") continue;
    events.push({
      id: `scan:${point.scanPointId}`,
      kind: "scan",
      label: point.checkpointName ?? "Check-in",
      occurredAt: point.serverReceivedAt,
      lat: point.lat,
      lng: point.lng,
    });
  }

  const punchRows = punchData.filter(
    (row): row is Record<string, unknown> => row !== null && typeof row === "object",
  );
  const punchIds = punchRows
    .map((row) => asText(row.time_punch_id))
    .filter((id): id is string => id !== null);
  if (punchIds.length > 0) {
    const points = await fetchTimeclockLocationPoints(client, accountId, punchIds);
    if (points.status === "found") {
      const byId = new Map(points.points.map((point) => [point.timePunchId, point]));
      for (const row of punchRows) {
        const id = asText(row.time_punch_id);
        const point = id === null ? undefined : byId.get(id);
        if (id === null || point === undefined) continue;
        const type = asText(row.punch_type) ?? "Time punch";
        const guard = asText(row.guard_label);
        events.push({
          id: `punch:${id}`,
          kind: "punch",
          label: `${type.replaceAll("_", " ")}${guard === null ? "" : ` · ${guard}`}`,
          occurredAt: asText(row.server_received_at) ?? asText(row.device_time),
          lat: point.lat,
          lng: point.lng,
        });
      }
    }
  }

  for (const raw of ladderData) {
    if (raw === null || typeof raw !== "object") continue;
    const row = raw as Record<string, unknown>;
    const callId = asText(row.duty_call_id);
    const seq = asNum(row.event_seq);
    const type = asText(row.event_type);
    // No GPS proof, no tick. A ladder rung without a fix has no place on a map.
    if (callId === null || seq === null || type === null || asText(row.gps_captured_at) === null) {
      continue;
    }
    events.push({
      id: `call:${callId}:${seq}`,
      kind: "ladder",
      label: type.replaceAll("_", " "),
      occurredAt: asText(row.recorded_at),
      lat: asNum(row.lat),
      lng: asNum(row.lng),
    });
  }

  for (const raw of sosData) {
    if (raw === null || typeof raw !== "object") continue;
    const row = raw as Record<string, unknown>;
    const assignmentId = asText(row.route_assignment_id);
    const eventId = asText(row.alert_event_id);
    if (asText(row.alert_type) !== "sos" || assignmentId === null || eventId === null) continue;
    const guard = asText(row.guard_label);
    events.push({
      // Keyed on the ASSIGNMENT first, so the twelve-tape ranker can recognise whose tape
      // carries this SOS without a second lookup.
      id: `sos:${assignmentId}:${eventId}`,
      kind: "sos",
      label: `SOS${guard === null ? "" : ` · ${guard}`}`,
      occurredAt: asText(row.detected_at),
      lat: asNum(row.duty_lat),
      lng: asNum(row.duty_lng),
    });
  }

  return { status: "ok", events: mergeCommandFocusEvents([events]) };
}

// --- Day index (lock 2026-08-24: Replay chrome + day index) ---------------------------------
//
// The calendar must light only the days that hold a tape for the CURRENT subject, and it must
// not learn that by reading one full tape per day. The host answers with dates only, clamped to
// the live account retention on its side; everything below is the Console half of that contract.
//
// Three honesty rules:
//
// 1. Subject-scoped, never account-wide. The index is keyed on the same scope the tape read
//    uses, and a subject change clears it before the next read starts, so another subject's lit
//    days are never on the glass.
// 2. Binary. A day has data or it does not. No counts, no density, no tick-only marks.
// 3. Fail closed. A refused or malformed index lights nothing; an empty calendar with a printed
//    reason is honest, a guessed one is the bug this slice exists to close.

/** Cell copy for a day inside retention with no pings for the subject. Lock copy, byte-identical. */
export const REPLAY_NO_DATA_DAY = "No recorded positions this day";

/** Panel copy when the subject has no lit day inside the window. Lock copy, byte-identical. */
export const REPLAY_SUBJECT_EMPTY = "Nothing recorded for this subject in the Replay window.";

export const REPLAY_DAY_INDEX_LOADING = "Finding days with recorded positions.";

export const REPLAY_DAY_INDEX_FAILED = "Could not load the days with recorded positions.";

/**
 * Host bound on the site scope, mirrored so an oversized roster is refused here with a sentence
 * instead of a 22023 the operator cannot read.
 */
export const REPLAY_DAY_INDEX_MAX_ASSIGNMENTS = 1000;

/** The exact scope a day index is read for. Same shapes the tape read scopes by. */
export type ReplayDayIndexScope =
  | { kind: "site"; routeAssignmentIds: readonly string[] }
  | { kind: "officer"; routeAssignmentIds: readonly string[] }
  | { kind: "call"; callId: string }
  | { kind: "unassigned" };

export type ReplayCalendarDayState = "has-data" | "no-data" | "outside" | "future";

const REPLAY_DAY_VALUE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/** A local calendar day as the panel and the host both spell it: YYYY-MM-DD. */
export function isReplayDayValue(value: unknown): value is string {
  return typeof value === "string" && REPLAY_DAY_VALUE_PATTERN.test(value);
}

/** Local YYYY-MM-DD for an instant. Local, because the operator's calendar is. */
export function replayDayValue(atMs: number): string {
  const day = new Date(atMs);
  const month = `${day.getMonth() + 1}`.padStart(2, "0");
  const date = `${day.getDate()}`.padStart(2, "0");
  return `${day.getFullYear()}-${month}-${date}`;
}

/** Local midnight for a YYYY-MM-DD value, or null when the value is not a day. */
export function replayDayStartMs(dayValue: string): number | null {
  if (!isReplayDayValue(dayValue)) return null;
  const [year, month, date] = dayValue.split("-").map((part) => Number(part));
  if (year === undefined || month === undefined || date === undefined) return null;
  const startMs = new Date(year, month - 1, date).getTime();
  // A day that does not exist (2026-02-31) rolls over in Date; refuse it rather than replay
  // whatever March day it rolled into.
  return Number.isFinite(startMs) && replayDayValue(startMs) === dayValue ? startMs : null;
}

/**
 * The day Replay opens on: the most recent lit day, or null when the subject has none.
 *
 * Never "today". Today is empty for most subjects most of the time, and opening on it is exactly
 * the silent-day-after-Play experience the lock closes.
 */
export function replayDefaultDay(days: readonly string[]): string | null {
  let newest: string | null = null;
  for (const day of days) {
    if (!isReplayDayValue(day)) continue;
    if (newest === null || day > newest) newest = day;
  }
  return newest;
}

/**
 * What a calendar cell is, for the subject whose lit days are given.
 *
 * ISO day strings compare correctly as strings, so the retention floor and today are compared
 * without a second Date parse. The floor day itself counts as inside: the host clamps at an
 * instant, and a ping later that day is a real tape.
 */
export function replayCalendarDayState(
  dayValue: string,
  options: { days: ReadonlySet<string> | readonly string[]; nowMs: number; retentionDays: number },
): ReplayCalendarDayState {
  const todayValue = replayDayValue(options.nowMs);
  const floorValue = replayDayValue(
    replayRetentionFloorMs(options.nowMs, replayRetentionDays(options.retentionDays)),
  );
  if (dayValue > todayValue) return "future";
  if (dayValue < floorValue) return "outside";
  const lit = options.days instanceof Set ? options.days : new Set(options.days);
  return lit.has(dayValue) ? "has-data" : "no-data";
}

/** Stable key for one index read, so a late answer for another scope is dropped. */
export function replayDayIndexScopeKey(scope: ReplayDayIndexScope): string {
  if (scope.kind === "site") return `site:${[...scope.routeAssignmentIds].sort().join(",")}`;
  if (scope.kind === "officer") {
    return `officer:${[...scope.routeAssignmentIds].sort().join(",")}`;
  }
  if (scope.kind === "call") return `call:${scope.callId}`;
  return "unassigned";
}

/**
 * The operator's IANA time zone, sent so the host cuts days where this calendar cuts them. UTC
 * when the runtime cannot say, which the host accepts explicitly.
 */
export function replayDeskTimeZone(): string {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return typeof zone === "string" && zone.trim().length > 0 ? replayCanonicalTimeZone(zone) : "UTC";
  } catch {
    return "UTC";
  }
}

/**
 * Legacy tzdata names a browser can still resolve. Each carries a digit, which the host's zone
 * shape gate refuses (a POSIX zone spec needs a digit, and the gate keeps those away from the
 * zone cache), so each is sent as the IANA name with the same rules. Nothing here moves the
 * clock the calendar is drawn on: the browser keeps its own zone, only the name changes.
 */
const REPLAY_LEGACY_ZONE_NAMES: Readonly<Record<string, string>> = {
  EST5EDT: "America/New_York",
  CST6CDT: "America/Chicago",
  MST7MDT: "America/Denver",
  PST8PDT: "America/Los_Angeles",
  GMT0: "UTC",
  "GMT+0": "UTC",
  "GMT-0": "UTC",
  "Etc/GMT0": "UTC",
  "Etc/GMT+0": "UTC",
  "Etc/GMT-0": "UTC",
};

/** The zone name the host is asked to cut days in: a legacy alias becomes its IANA equivalent. */
export function replayCanonicalTimeZone(zone: string): string {
  const trimmed = zone.trim();
  return REPLAY_LEGACY_ZONE_NAMES[trimmed] ?? trimmed;
}

/**
 * Days inside retention with at least one eligible ping for the scope, newest first.
 *
 * One bounded RPC per subject pick. The host clamps retention from the live policy and applies
 * the tape audience gates; this side refuses to build a calendar from anything but the contract:
 * a row that carries more than its date (a coordinate, an id, a count) is a broken response and
 * the whole index is refused rather than painted from.
 *
 * A site with no roster assignment has nothing the tape read could draw, so it resolves to no
 * days without a request rather than asking the host a question it will refuse.
 */
export async function fetchReplayDayIndex(
  client: SupabaseClient,
  accountId: string,
  options: { scope: ReplayDayIndexScope; timeZone: string },
): Promise<string[]> {
  const { scope } = options;
  const failure = () => new Error(REPLAY_DAY_INDEX_FAILED);
  let assignmentIds: string[] | null = null;
  let callId: string | null = null;
  if (scope.kind === "site") {
    assignmentIds = [...new Set(scope.routeAssignmentIds)].filter((id) => id.length > 0);
    if (assignmentIds.length === 0) return [];
    if (assignmentIds.length > REPLAY_DAY_INDEX_MAX_ASSIGNMENTS) throw failure();
  } else if (scope.kind === "officer") {
    assignmentIds = [...new Set(scope.routeAssignmentIds)].filter((id) => id.length > 0);
    if (assignmentIds.length === 0) return [];
    if (assignmentIds.length > REPLAY_DAY_INDEX_MAX_ASSIGNMENTS) throw failure();
  } else if (scope.kind === "call") {
    if (scope.callId.length === 0) return [];
    callId = scope.callId;
  }
  const { data, error } = await client.rpc("list_replay_days", {
    active_account_id: accountId,
    p_subject_kind: scope.kind,
    p_route_assignment_ids: assignmentIds,
    p_duty_call_id: callId,
    p_time_zone: replayCanonicalTimeZone(options.timeZone),
  });
  if (error !== null || !Array.isArray(data)) throw failure();
  const days = new Set<string>();
  for (const raw of data) {
    if (typeof raw !== "object" || raw === null || Array.isArray(raw)) throw failure();
    const row = raw as Record<string, unknown>;
    const keys = Object.keys(row);
    if (keys.length !== 1 || keys[0] !== "replay_day") throw failure();
    const value = row.replay_day;
    // The host returns a date; PostgREST spells it YYYY-MM-DD. Anything longer (a timestamp,
    // an offset) is not the contract and would let a lit day sit one day off from its tape.
    if (!isReplayDayValue(value)) throw failure();
    days.add(value);
  }
  return [...days].sort((left, right) => (left < right ? 1 : left > right ? -1 : 0));
}
